import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, ArrowUpRightIcon, CalendarIcon, MapPinIcon, SunIcon } from 'lucide-react';
import { destinations } from '../../data/destinations';
import { courses } from '../../data/courses';
import { MockupTag } from '../ui/MockupTag';
import { destinationPath } from '../../utils/paths';
import type { Destination, Season } from '../../types/content';
function programmeCount(destination: Destination, season: Season) {
  return courses.filter(course => course.season === season && course.locations.includes(destination.slug)).length;
}
export function DestinationShowcase() {
  const yearRound = destinations.filter(d => d.yearRound);
  const seasonal = destinations.filter(d => d.seasonal);
  const track = useRef<HTMLUListElement>(null);
  function scroll(direction: 1 | -1) {
    const el = track.current;
    if (!el) return;
    el.scrollBy({
      left: direction * Math.min(el.clientWidth * 0.8, 640),
      behavior: 'smooth'
    });
  }
  return <div className="space-y-16" data-id="element-431" data-mp="ftc6nrrr6kfm">
      <div data-id="element-432" data-mp="ftlup9t8mhio">
        <GroupHeader icon={CalendarIcon} title="Year-round campuses" text="Start any Monday. English, exam and professional courses run every week of the year." data-id="element-433" data-mp="b1ccyh2a0nrh" />
        <ul className="mt-6 grid gap-5 lg:grid-cols-12" data-id="element-434" data-mp="6294snr021fa">
          {yearRound.map((destination, index) => <li key={destination.slug} className={index === 0 ? 'lg:col-span-7' : 'lg:col-span-5'} data-id="element-435" data-mp="10s8z6ndh55k" data-mpi={index}>
              <Link to={destinationPath(destination, 'year-round')} className="group relative flex h-[420px] flex-col justify-between overflow-hidden rounded-[28px] p-5 md:h-[480px] md:p-7" data-id="element-436" data-mp="yoapj3n9zbk4">
                <img src={destination.image} alt={`View of ${destination.name}`} className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 ease-brand group-hover:scale-[1.03]" data-id="element-437" data-mp="ju5u6k1a9x7z" />
                <div className="absolute inset-0 bg-black/45" aria-hidden="true" data-id="element-438" data-mp="1wkfpwr7c4cub" />
                <div className="relative flex items-start justify-between gap-3" data-id="element-439" data-mp="1wibpwkck3xzb">
                  <MockupTag note="Placeholder image" data-id="element-440" data-mp="20kyhql989iwi" />
                  <span className="flex h-12 w-12 items-center justify-center rounded-[15px] bg-white text-ink transition-colors duration-150 group-hover:bg-brand group-hover:text-white" aria-hidden="true" data-id="element-441" data-mp="1mmrsfticgr8g">
                    <ArrowUpRightIcon className="h-5 w-5" data-id="element-442" data-mp="1cv47mbgam8fy" />
                  </span>
                </div>
                <div className="relative" data-id="element-443" data-mp="1hnp099ngqfhw">
                  <p className="inline-flex items-center gap-1.5 type-small font-semibold text-white" data-id="element-444" data-mp="1hoqe1lvv5g3b">
                    <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-445" data-mp="18ule8cyr4ubn" />
                    {destination.country}
                  </p>
                  <h3 className="mt-2 type-hero text-white" data-id="element-446" data-mp="tx1ebkgpppcc">{destination.name}</h3>
                  <div className="mt-4 flex flex-wrap gap-2" data-id="element-447" data-mp="ywxibjpetgrw">
                    <Chip data-id="element-448" data-mp="yyibp5zzmto6">{programmeCount(destination, 'year-round')} programmes</Chip>
                    <Chip data-id="element-449" data-mp="faw0naf8ekp9">
                      {destination.campuses.length} {destination.campuses.length > 1 ? 'campuses' : 'campus'}
                    </Chip>
                    <Chip data-id="element-450" data-mp="p91x3ci9qymq">Starts every Monday</Chip>
                  </div>
                </div>
              </Link>
            </li>)}
        </ul>
      </div>

      <div id="seasonal-locations" data-id="element-451" data-mp="y9gmamycsxvi">
        <GroupHeader icon={SunIcon} title="Seasonal programmes" text="Camps, mini-stays and short courses on set summer and winter dates, with trips and activities." controls={<div className="flex gap-2" data-id="element-453" data-mp="17yrvsap5k4jx">
              <button type="button" onClick={() => scroll(-1)} aria-label="Show previous locations" className="flex h-11 w-11 items-center justify-center rounded-[15px] border border-ink bg-white text-ink transition-colors duration-150 hover:bg-mist" data-id="element-454" data-mp="1306vkb9whkvh">
                <ArrowLeftIcon className="h-5 w-5" data-id="element-455" data-mp="36g2194aotxem" />
              </button>
              <button type="button" onClick={() => scroll(1)} aria-label="Show more locations" className="flex h-11 w-11 items-center justify-center rounded-[15px] bg-ink text-white transition-colors duration-150 hover:bg-brand" data-id="element-456" data-mp="3bfwo8wrce2ue">
                <ArrowRightIcon className="h-5 w-5" data-id="element-457" data-mp="2nir5hto9lm3x" />
              </button>
            </div>} data-id="element-452" data-mp="y8f0bnfsuqz0" />
        <MockupTag note="Placeholder images" className="mt-6" data-id="element-458" data-mp="2sidfzvcpko91" />
        <ul ref={track} className="-mx-5 mt-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-4 [scrollbar-width:none] md:-mx-8 md:px-8 [&::-webkit-scrollbar]:hidden" data-id="element-459" data-mp="2xgeyof682ljz">
          {seasonal.map(destination => <li key={destination.slug} className="w-[240px] shrink-0 snap-start md:w-[280px]" data-id="element-460" data-mp="cqzazt2avcyk">
              <Link to={destinationPath(destination, 'seasonal')} className="group relative flex aspect-[3/4] flex-col justify-end overflow-hidden rounded-[24px] p-5" data-id="element-461" data-mp="horbhbh0g7ed">
                <img src={destination.image} alt={`View of ${destination.name}`} className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 ease-brand group-hover:scale-[1.04]" data-id="element-462" data-mp="2294nrwyyg6p0" />
                <div className="absolute inset-0 bg-black/40" aria-hidden="true" data-id="element-463" data-mp="1ni5iautf1y9n" />
                <div className="relative" data-id="element-464" data-mp="1ikvufjlodunx">
                  <p className="type-small font-semibold text-white" data-id="element-465" data-mp="1ehqt3kdbzz0v">{destination.country}</p>
                  <h3 className="mt-1 type-h2 text-white" data-id="element-466" data-mp="19imkovy8e5bl">{destination.name}</h3>
                  <p className="mt-3 inline-flex items-center gap-1.5 rounded-[10px] bg-white px-2.5 py-1 type-fine font-bold text-ink" data-id="element-467" data-mp="19kqn19ghew9r">
                    <SunIcon className="h-3.5 w-3.5 text-brand" aria-hidden="true" data-id="element-468" data-mp="33x712wfirep6" />
                    {programmeCount(destination, 'seasonal')} seasonal programmes
                  </p>
                </div>
              </Link>
            </li>)}
        </ul>
      </div>
    </div>;
}
function Chip({
  children
}: {
  children: React.ReactNode;
}) {
  return <span className="rounded-[10px] bg-white px-3 py-1.5 type-fine font-bold text-ink" data-id="element-469" data-mp="1217x0j8tm2ro">{children}</span>;
}
function GroupHeader({
  icon: Icon,
  title,
  text,
  controls
}: {
  icon: React.ComponentType<{
    className?: string;
  }>;
  title: string;
  text: string;
  controls?: React.ReactNode;
}) {
  return <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between md:gap-10" data-id="element-470" data-mp="1fx1rvycrerls">
      <div className="flex items-center gap-3" data-id="element-471" data-mp="1kvmwe9z1nxg1">
        <span className="flex h-11 w-11 items-center justify-center rounded-[13px] bg-brand text-white" aria-hidden="true" data-id="element-472" data-mp="15zovkee6e4xh">
          <Icon className="h-5 w-5" data-id="element-473" data-mp="1axypyms2t1i1" />
        </span>
        <div data-id="element-474" data-mp="3qqfu1k9ynlji">
          <h3 className="type-h3 text-ink" data-id="element-475" data-mp="3qpway08kqr3w">{title}</h3>
          <p className="mt-1 type-card-desc" data-id="element-476" data-mp="3qtac555i4u9r">{text}</p>
        </div>
      </div>
      {controls}
    </div>;
}